import { Divider } from '@blueprintjs/core'
import React from 'react'
import styled from 'styled-components'
import { formatBRL } from '../../cash-register/common/formatBRL'
import { black, blue, gray } from '../../ui/utils/_colors'
import { InfoBox } from '../../atoms/Box/InfoBox'
import { ReactComponent as AlertIconFilledSmall } from '../../common/assets/images/alert-icon-filled-small.svg'
import { convertToPercentage, resolveFeeByDueDateRule } from '../../utils/math.utils'
import { DueDateRule } from '../../services/bff/coordinator/dto/Contracts.dto'
import { ListItem } from './components/ListItem'
import { DateUtils } from '../../common/date-utils'

interface Props {
  principal: number
  lateInterestRate: number
  iof: number
  numberOfInstallments: number
  firstInstallmentDueDate?: string
  installmentValue: number
  installmentValueWithoutInterest?: number
  installmentValueWithoutBonification: number
  totalPurchaseValue: number
  couponDiscount: number
  dueDateRule?: DueDateRule
}

export const InterestPaidByRetailerPurchaseDetails: React.FC<Props> = ({
  principal,
  lateInterestRate,
  iof,
  numberOfInstallments,
  firstInstallmentDueDate,
  installmentValue,
  installmentValueWithoutInterest,
  installmentValueWithoutBonification,
  totalPurchaseValue,
  dueDateRule,
}) => {
  return (
    <MainContainer>
      <DetailsContainer>
        <Title>Taxas e juros aplicados</Title>
        <ListItem
          label={'Valor da compra'}
          value={formatBRL(principal)}
          valueProps={{ weight: '400' }}
          padding="8px 16px"
        />
        <ListItem
          label={'IOF'}
          value={formatBRL(iof)}
          valueProps={{ weight: '400' }}
          padding="8px 16px"
        />
        <ListItem
          label={'Valor total do contrato'}
          labelProps={{ weight: '600' }}
          value={formatBRL(totalPurchaseValue)}
          padding="8px 16px"
        />

        <Divider />

        <Title>Parcelamento</Title>
        <ListItem
          label={'Parcelamento escolhido'}
          value={`${numberOfInstallments}x`}
          valueProps={{ weight: '400' }}
          padding="8px 16px"
        />
        <ListItem
          label={'Valor da parcela no vencimento'}
          value={formatBRL(installmentValueWithoutInterest!)}
          valueProps={{ weight: '400' }}
          padding="8px 16px"
        />
        <ListItem
          label={'Juros de atraso da parcela¹'}
          value={`${convertToPercentage(
            resolveFeeByDueDateRule(lateInterestRate, dueDateRule)
          )}% a.m.`}
          valueProps={{ weight: '400' }}
          padding="8px 16px"
        />
        <ListItem
          label={'Valor da parcela sem bonificação'}
          value={formatBRL(installmentValueWithoutBonification)}
          valueProps={{ weight: '400' }}
          padding="8px 16px"
        />

        <InfoBoxContainer>
          <InfoBox
            maxWidth="100%"
            backgroundColor={blue.alert50}
            backgroundBorderColor={blue.translucent}
            icon={<AlertIconFilledSmall style={{ marginTop: 1 }} />}
            title="Condições de pagamento"
            text={
              '¹Em caso de atraso no pagamento, cliente terá seu limite bloqueado e deve pagar os juros da compra integralmente.'
            }
          />
        </InfoBoxContainer>

        <Divider />

        <Title>Primeiro pagamento</Title>
        <ListItem
          label={'Data da 1ª parcela'}
          value={DateUtils.formatDate(firstInstallmentDueDate!, 'L')}
          padding="8px 16px"
        />
        <ListItem
          label={'Primeira parcela'}
          value={formatBRL(installmentValue)}
          padding="8px 16px"
        />
      </DetailsContainer>
    </MainContainer>
  )
}

export default InterestPaidByRetailerPurchaseDetails

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  align-items: center;
  gap: 24px;
`

const DetailsContainer = styled.div`
  width: 100%;
  margin: 16px auto;
  padding: 16px;
  gap: 10px;

  border-radius: 16px;
  background: ${gray.light2};
`

const Title = styled.div`
  padding: 24px 16px 8px 16px;
  text-align: left;

  font-family: Inter;
  font-size: 16px;
  font-weight: 700;
  color: ${black.text2};
  line-height: 22px;
`

const InfoBoxContainer = styled.div`
  padding: 16px;
`
